// Experiences, food, pool, campfire, nearby places, reviews, gallery — CMS-ready

export const ABOUT_RESORT = {
  overline: "Welcome to Vruksha Valley",
  heading: "A luxury eco retreat in the heart of the Western Ghats",
  paragraphs: [
    "Vruksha Valley is a luxury eco-resort located in Kalasa, Chikkamagaluru, right in the heart of the Western Ghats. Built within a lush coffee estate, it is designed for guests who want to enjoy nature, peace and a little adventure — all at once.",
    "Just a short drive from the beautiful Soormane Falls, the resort makes a perfect base for exploring Netravati Peak, Kudremukh, Horanadu and Kyatanamakki. Wake to mist over the ridgelines, lounge by the infinity pool, gather around the bonfire, and let time slow down — whether you arrive as a couple, a family, or a big group of friends.",
  ],
  highlights: [
    "Infinity pool with valley views",
    "Nestled inside a coffee estate",
    "Stunning Western Ghats panoramas",
    "Bonfire & campfire evenings",
    "Authentic Malnad cuisine",
    "Indoor games & lounge",
  ],
};

export const INDOOR_GAMES = {
  title: "Indoor Games & Lounge",
  intro:
    "When the mist rolls in, the fun moves indoors. Our games lounge keeps groups and families entertained with carrom, chess, table games and more — perfect for lazy afternoons and lively evenings.",
};

export const FOOD = {
  title: "Authentic Malnad Food",
  intro:
    "Dining at Vruksha Valley is a celebration of Malnad's forest kitchens. Every meal is home-cooked with produce from the surrounding coffee estates and served warm, generous and full of flavour — the way the hills have fed travellers for generations. From a hearty South Indian breakfast to a complete thali lunch and a crackling non-veg spread, you will never leave the table hungry.",
  images: [
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/62l4t0k3_food%20new%2001.jfif",
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/kmw6irca_food%20new%2002.jfif",
    "https://images.pexels.com/photos/32797056/pexels-photo-32797056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/35539324/pexels-photo-35539324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ],
  dishes: ["Full South Indian Breakfast", "Complete Veg & Non-Veg Thali", "Malnad-style Akki Rotti", "Ghee & Jeera Flavoured Rice", "Chicken Gravy & Kababs", "Estate Filter Coffee"],
  menu: [
    {
      heading: "Breakfast",
      note: "A full South Indian spread",
      items: [
        "Idli–Vada / Dosa / Poori Saagu / Kadubu (Malnad style)",
        "Rice varieties — Veg Pulav / Puliyogare / Lemon Rice / Vangi Bath",
        "Bread Toast",
        "Fresh Fruit Cutting",
        "Coffee / Tea",
      ],
    },
    {
      heading: "Lunch & Dinner — Veg",
      note: "Complete thali-style meals",
      items: [
        "Chapati / Akki Rotti",
        "Veg Gravy (Sambar / Dal)",
        "Rice & Rasam",
        "Palya (seasonal vegetable)",
        "Veg Starter (Manchuri / Bajji)",
        "Flavoured Rice (Jeera / Ghee)",
        "Sweet (Payasa)",
        "Salad · Curd / Buttermilk",
      ],
    },
    {
      heading: "Lunch & Dinner — Non-Veg",
      note: "Everything in the veg thali, plus",
      items: [
        "Chicken Gravy (Red / Green)",
        "Non-Veg Starter (Kabab / Sukka / Pepper)",
      ],
    },
    {
      heading: "Evening Snacks",
      note: "Quick bites as the mist rolls in",
      items: ["Pakoda / Bajji", "Hot Tea / Coffee"],
    },
  ],
};

export const POOL = {
  title: "Swimming Pool & Infinity Pool",
  intro:
    "Two pools, one unforgettable view. Float in our infinity pool where the water seems to spill straight into the valley, or unwind in the second pool beside it. It is the beating heart of the resort — the spot every guest falls in love with first.",
  couplesFamilies:
    "Our pools are made for togetherness. Couples drift here for a quiet sunrise swim and golden-hour photographs, while families and big groups of friends spend whole afternoons splashing, lounging on the deck and soaking in the mountain air. Warm water, safe surrounds and that endless Western Ghats horizon make it perfect for every kind of guest.",
  sightseeing:
    "There is no better seat for watching the Western Ghats than the edge of our pool. Sip a coffee as clouds move through the peaks, spot distant waterfalls after the rains, and let the mountain air do the rest. Morning mist, afternoon sun and violet dusks — the view changes all day, and it is always beautiful.",
  images: [
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/vvvlqwg6_swimming%2001.jfif",
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/erurrcel_swmming%203.jfif",
  ],
};

export const CAMPFIRE = {
  title: "Campfire & Bonfire Nights",
  intro:
    "High in the Western Ghats near Kalasa, the evenings belong to the fire. As the temperature dips and the stars come out over the coffee estate, we light the bonfire and the whole resort gathers around it. There is nothing quite like the warmth of the flames against the cool mountain night.",
  moreWords:
    "Our bonfire nights are made for connection — couples wrapped in a blanket under a canopy of stars, groups of friends swapping stories and songs, families laughing late into the night with hot drinks in hand. It is the moment guests remember long after they leave: the crackle of the fire, the smell of the forest and a sky full of stars, deep in the heart of Malnad.",
  images: [
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/7g4dnltp_camp.jfif",
    "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/16fzxx8j_camp%202.jfif",
  ],
};

export const GAMES = [
  { icon: "🎱", name: "Snooker", text: "A classic cue sport that tests your precision, strategy and focus. Perfect for friendly matches and relaxing indoor entertainment." },
  { icon: "🏓", name: "Table Tennis", text: "A fast-paced game that improves reflexes and coordination. Ideal for players of all ages and skill levels." },
  { icon: "⚽", name: "Foosball", text: "An exciting tabletop football game where two or four players compete in a fun and competitive match." },
  { icon: "♟️", name: "Chess", text: "A timeless strategy game that challenges your thinking, planning and decision-making skills." },
  { icon: "🎯", name: "Carrom", text: "A traditional indoor board game enjoyed by family and friends, combining skill, accuracy and fun." },
  { icon: "🎾", name: "Swingball (Tether Tennis)", text: "A thrilling two-player game where a ball attached to a rope is hit around a pole. Fast-paced and entertaining — great for all age groups." },
];

// Fast-sliding nearby attractions (name + image + approx. distance by road).
export const NEARBY = [
  { name: "Soormane Waterfalls", distance: "300 m", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/yxr9vy3c_soormane%20fals.jfif" },
  { name: "Kalasa Temple", distance: "5.5 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/tdi6q7ab_kalsa%20temple.jfif" },
  { name: "Hanging Bridge", distance: "5 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/74ek4n9g_hanging%20bridge%20kalsa.jfif" },
  { name: "Horanadu Temple", distance: "11 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/56h7p68b_horanadu%20temple.jfif" },
  { name: "Samse Tea Estate", distance: "13 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/0yhnqd04_samse%20t%20esate.jfif" },
  { name: "Netravati Peak", distance: "15 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/hdond4g5_netravathi.jfif" },
  { name: "Kudremukh Peak", distance: "25 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/bruc18cc_kuduremukha.jfif" },
  { name: "Rani Jari Falls", distance: "32 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/8491aoki_rani%20jri.jfif" },
  { name: "Kurinjal Peak", distance: "33 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/4o6nh9mh_kurinjal.jfif" },
  { name: "Gangadikallu Peak", distance: "38 km", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/i8jjm8a0_gangadikallu%20peak.jfif" },
];

export const REVIEWS = [
  { name: "Ananya Rao", from: "Bengaluru", rating: 5, text: "The glass cottage and the mist at dawn felt unreal. Waking up to the valley view with filter coffee is something I still think about." },
  { name: "Rahul Menon", from: "Kochi", rating: 5, text: "The Malnad food was the highlight — every meal tasted like a grandmother's kitchen. The bonfire night sealed it for our whole group." },
  { name: "Sneha & Vikram", from: "Mumbai", rating: 5, text: "Perfect anniversary escape. The infinity pool at sunset and the warmth of the hosts made it feel like a private retreat." },
  { name: "Karthik Shetty", from: "Mangaluru", rating: 5, text: "Clean, calm and genuinely close to nature. Soormane Waterfalls nearby was a bonus. Will return every monsoon." },
  { name: "Priya Nair", from: "Hyderabad", rating: 5, text: "Everything about Vruksha Valley is thoughtful — from the timber cottages to the campfire. Truly a cinematic getaway." },
  { name: "The Kamath Family", from: "Udupi", rating: 5, text: "Came as a group of twelve, stayed in the dormitory and cottages. Indoor games kept the kids happy and the pool kept the adults happy!" },
];

export const GALLERY = [
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_5dd34c9f-6dff-45ce-a6d1-857af95232c8/artifacts/4whvboid_image%20no1.png",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/9vrw8jq4_3.jfif",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/brpn7t9e_swimming%20pool.jfif",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/s3mjv4zi_4.jfif",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/62l4t0k3_food%20new%2001.jfif",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/kmw6irca_food%20new%2002.jfif",
  "https://images.pexels.com/photos/32797056/pexels-photo-32797056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/35539324/pexels-photo-35539324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/15935916/pexels-photo-15935916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1534958188388-fa3bd087d97e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxuaWdodCUyMGNhbXBmaXJlJTIwaW4lMjBmb3Jlc3R8ZW58MHx8fHwxNzg0OTU0MjczfDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/14025910/pexels-photo-14025910.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];
